import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Sparkles, Baby, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const PLAN_OPTIONS = {
  soulai_coach: [
    { value: 'monthly', label: 'Monthly (30 days)' },
    { value: 'till_delivery', label: 'Till Delivery (EDD + 3 days)' },
    { value: 'elite_addon', label: 'Elite add-on (EDD + 3 days)' },
    { value: 'manual', label: 'Custom days' },
  ],
  baby_tracker: [
    { value: 'monthly', label: 'Monthly (30 days)' },
    { value: 'yearly', label: 'Yearly (365 days)' },
    { value: 'elite_addon', label: 'Elite add-on (365 days)' },
    { value: 'manual', label: 'Custom days' },
  ],
};

const LEVEL_STYLE = {
  full: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  free: 'bg-amber-50 text-amber-800 border-amber-200',
  trial: 'bg-sky-50 text-sky-800 border-sky-200',
  trial_available: 'bg-slate-50 text-slate-700 border-slate-200',
  read_only: 'bg-rose-50 text-rose-800 border-rose-200',
  none: 'bg-slate-50 text-slate-600 border-slate-200',
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : null);

const describe = (product, a) => {
  if (!a) return '';
  if (a.level === 'full') {
    const plan = { monthly: 'Monthly', till_delivery: 'Till Delivery', yearly: 'Yearly', elite_addon: 'Elite add-on', manual: 'Manual grant' }[a.plan] || (a.source === 'admin_override' ? 'Admin toggle' : 'Active');
    return `${plan}${a.expires_at ? ` · till ${fmtDate(a.expires_at)}` : ''}${a.days_left != null ? ` (${a.days_left}d)` : ''}`;
  }
  if (product === 'soulai_coach') {
    if (a.level === 'free') return a.paywall ? 'Free tier · free class done → paywalled' : 'Free tier · first class unlocked';
    return 'Coming Soon (no access)';
  }
  if (a.level === 'trial') return `7-day trial · ${a.days_left}d left`;
  if (a.level === 'trial_available') return 'Trial not started';
  if (a.level === 'read_only') return `Trial ended ${fmtDate(a.trial_ends_at)} · read-only`;
  return a.level;
};

const ProductRow = ({ product, icon: Icon, label, access, onGrant, onRevoke, busy }) => (
  <div className="rounded-lg bg-white/60 border border-border/60 px-2.5 py-2 space-y-1.5" data-testid={`product-row-${product}`}>
    <div className="flex items-center gap-2">
      <Icon className="h-3.5 w-3.5 text-primary shrink-0" />
      <span className="text-xs font-medium">{label}</span>
      <div className="ml-auto flex gap-1.5">
        <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => onGrant(product)} disabled={busy} data-testid={`product-grant-${product}`}>Grant</Button>
        {(access?.level === 'full' && access?.source !== 'admin_override') && (
          <Button variant="outline" size="sm" className="text-xs h-7 text-rose-700 border-rose-200 hover:bg-rose-50" onClick={() => onRevoke(product)} disabled={busy} data-testid={`product-revoke-${product}`}>Revoke</Button>
        )}
      </div>
    </div>
    <Badge variant="outline" className={`text-[10px] font-normal ${LEVEL_STYLE[access?.level] || ''}`} data-testid={`product-level-${product}`}>
      {describe(product, access)}
    </Badge>
  </div>
);

export const ProductAccessControl = ({ userId }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [grant, setGrant] = useState(null); // {product}
  const [plan, setPlan] = useState('monthly');
  const [days, setDays] = useState('30');
  const [reason, setReason] = useState('');

  const load = useCallback(() => {
    if (!userId) return;
    api.get(`/api/admin/products/users/${userId}`).then((r) => setData(r.data)).catch(() => {});
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  const doGrant = async () => {
    setBusy(true);
    try {
      const body = { product: grant.product, plan, reason: reason || undefined };
      if (plan === 'manual') body.duration_days = Number(days);
      const r = await api.post(`/api/admin/products/users/${userId}/grant`, body);
      setData(r.data); setGrant(null); setReason('');
      toast.success('Access granted');
    } catch (err) { toast.error(err.response?.data?.detail || 'Grant failed'); }
    setBusy(false);
  };

  const doRevoke = async (product) => {
    if (!window.confirm(`Revoke ${product === 'soulai_coach' ? 'SoulAI Coach' : 'Baby Tracker'} access for this user?`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/api/admin/products/users/${userId}/revoke`, { product, reason: 'Admin revoke' });
      setData(r.data); toast.success('Access revoked');
    } catch (err) { toast.error(err.response?.data?.detail || 'Revoke failed'); }
    setBusy(false);
  };

  if (!data) return null;
  return (
    <div className="mt-3 rounded-xl border bg-muted/20 p-3 space-y-2" data-testid="product-access-control">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Products</p>
        {!data.coach_public_launch && <span className="text-[10px] text-muted-foreground" data-testid="coach-launch-hint">Coach public launch: OFF</span>}
      </div>
      <ProductRow product="soulai_coach" icon={Sparkles} label="SoulAI Coach" access={data.soulai_coach} busy={busy}
        onGrant={(p) => { setGrant({ product: p }); setPlan('till_delivery'); }} onRevoke={doRevoke} />
      <ProductRow product="baby_tracker" icon={Baby} label="Baby Tracker" access={data.baby_tracker} busy={busy}
        onGrant={(p) => { setGrant({ product: p }); setPlan('yearly'); }} onRevoke={doRevoke} />

      <Dialog open={!!grant} onOpenChange={(v) => !v && setGrant(null)}>
        <DialogContent className="max-w-sm" data-testid="product-grant-dialog">
          <DialogHeader>
            <DialogTitle>Grant {grant?.product === 'soulai_coach' ? 'SoulAI Coach' : 'Baby Tracker'}</DialogTitle>
            <DialogDescription>Creates a product entitlement. Use this for payments collected outside the app.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Select value={plan} onValueChange={setPlan}>
              <SelectTrigger className="h-9" data-testid="product-grant-plan"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(PLAN_OPTIONS[grant?.product] || []).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {plan === 'manual' && (
              <Input type="number" min={1} max={1000} value={days} onChange={(e) => setDays(e.target.value)} placeholder="Days" className="h-9" data-testid="product-grant-days" />
            )}
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional)" className="h-9" data-testid="product-grant-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGrant(null)} disabled={busy}>Cancel</Button>
            <Button onClick={doGrant} disabled={busy || (plan === 'manual' && !(Number(days) > 0))} data-testid="product-grant-confirm">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Grant'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
